/**
 * Tenant Context Provider
 * Provides tenant configuration and industry information to all components
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getData } from '../utils/serverHelper';
import { SERVER_URL } from '../utils/secret';

const TenantContext = createContext();

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useSelector(state => state.auth);

  useEffect(() => {
    const loadTenantConfig = async () => {
      // Only load if user is logged in and has business_id
      if (!auth?.isLoggedIn || !auth?.user?.business_id || !auth?.token) {
        setLoading(false);
        setTenant(null);
        return;
      }

      try {
        // Check localStorage cache first
        const cacheKey = `tenant_config_${auth.user.business_id}`;
        const cached = localStorage.getItem(cacheKey);
        
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          // Short cache so super-admin changes to modules/names show up quickly
          if (Date.now() - timestamp < 60000) {
            setTenant(data);
            setLoading(false);
            return;
          }
        }

        // Fetch tenant config from API
        // getData returns axios response.data, which is { success: true, data: {...} } from ResponseHelper
        const response = await getData('/tenant/config');
        
        // Extract the actual data from response
        // ResponseHelper.ok() sends { success: true, data: {...} }
        // getData() returns response.data, so we get { success: true, data: {...} }
        // We need to extract the inner 'data' property
        const data = (response && response.success !== false) ? (response.data || response) : null;
        
        // Validate that we have the required fields
        if (data && !data.error && (data.industry || data.industry_key || data.labels)) {
          // Ensure labels and modules are properly structured
          const tenantData = {
            industry: data.industry || data.industry_key || 'clinic',
            industry_key: data.industry || data.industry_key || 'clinic',
            industry_name: data.industry_name || 'Clinic',
            business_id: data.business_id,
            labels: data.labels || {},
            modules: data.modules || {},
            config: data // Store full config for reference
          };
          
          // Clear old cache if industry changed
          const oldCache = localStorage.getItem(cacheKey);
          if (oldCache) {
            try {
              const oldData = JSON.parse(oldCache);
              if (oldData.data?.industry_key !== tenantData.industry_key) {
                console.log('Industry changed, clearing cache');
                localStorage.removeItem(cacheKey);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
          
          setTenant(tenantData);
          
          // Cache it
          localStorage.setItem(cacheKey, JSON.stringify({
            data: tenantData,
            timestamp: Date.now()
          }));
          
          console.log('Tenant config loaded:', {
            industry_key: tenantData.industry_key,
            industry_name: tenantData.industry_name,
            labels: tenantData.labels,
            modules_count: Object.keys(tenantData.modules).length
          });
        } else {
          console.warn('Failed to load tenant config - invalid response:', response);
          // Set default tenant config as fallback
          setTenant({
            industry: 'clinic',
            industry_name: 'Clinic',
            labels: {
              customer: 'Patient',
              customers: 'Patients',
              staff: 'Doctor',
              staff_plural: 'Doctors',
              appointment: 'Appointment'
            },
            modules: {
              appointments: { enabled: true },
              customers: { enabled: true },
              staff: { enabled: true },
              services: { enabled: true },
              products: { enabled: true },
              billing: { enabled: true },
              reports: { enabled: true },
              documents: { enabled: true }
            }
          });
        }
      } catch (error) {
        console.error('Failed to load tenant config:', error);
        // Set default tenant config as fallback
        setTenant({
          industry: 'clinic',
          industry_name: 'Clinic',
          labels: {
            customer: 'Patient',
            customers: 'Patients',
            staff: 'Doctor',
            staff_plural: 'Doctors',
            appointment: 'Appointment'
          },
          modules: {
            appointments: { enabled: true },
            customers: { enabled: true },
            staff: { enabled: true },
            services: { enabled: true },
            products: { enabled: true },
            billing: { enabled: true },
            reports: { enabled: true },
            documents: { enabled: true }
          }
        });
      } finally {
        setLoading(false);
      }
    };

    loadTenantConfig();
  }, [auth?.isLoggedIn, auth?.user?.business_id, auth?.token]);

  // Clear tenant when user logs out
  useEffect(() => {
    if (!auth?.isLoggedIn) {
      setTenant(null);
      setLoading(false);
    }
  }, [auth?.isLoggedIn]);

  return (
    <TenantContext.Provider value={{ tenant, loading }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  // Return default values instead of throwing error to prevent crashes
  if (!context) {
    console.warn('useTenant: Tenant context not available, returning defaults');
    return {
      tenant: null,
      loading: true
    };
  }
  return context;
};

